/**
 * PermissoesUsuarioView Component - User permissions view
 */
import React, { useState, useMemo } from 'react';
import { Users, Shield, Loader2, ChevronDown, Search, X } from 'lucide-react';
import type { User } from '../../services/userService';
import type { PermissaoEfetiva } from '../../services/permissoesService';

type TipoPermissao = 'pode_visualizar' | 'pode_criar' | 'pode_editar' | 'pode_excluir' | 'pode_exportar';

interface PermissoesUsuarioViewProps {
  usuarios: User[];
  selectedUsuario: User | null;
  permissoesUsuario: PermissaoEfetiva[];
  loadingUsuario: boolean;
  onSelectUsuario: (user: User) => void;
  onPermissaoChange: (
    telaId: number,
    tipoPermissao: TipoPermissao,
    valor: boolean
  ) => void;
}

const tiposPermissao = [
  { key: 'pode_visualizar' as const, label: 'V', title: 'Visualizar', color: 'blue' },
  { key: 'pode_criar' as const, label: 'C', title: 'Criar', color: 'green' },
  { key: 'pode_editar' as const, label: 'E', title: 'Editar', color: 'yellow' },
  { key: 'pode_excluir' as const, label: 'D', title: 'Excluir', color: 'red' },
  { key: 'pode_exportar' as const, label: 'X', title: 'Exportar', color: 'purple' }
];

export const PermissoesUsuarioView: React.FC<PermissoesUsuarioViewProps> = ({
  usuarios,
  selectedUsuario,
  permissoesUsuario,
  loadingUsuario,
  onSelectUsuario,
  onPermissaoChange
}) => {
  const [expandedModulos, setExpandedModulos] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  // Filter users based on search term
  const usuariosFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return usuarios;

    const termLower = searchTerm.toLowerCase().trim();
    return usuarios.filter(user =>
      user.username.toLowerCase().includes(termLower) ||
      user.email.toLowerCase().includes(termLower)
    );
  }, [usuarios, searchTerm]);

  const toggleModule = (modulo: string) => {
    setExpandedModulos(prev => {
      const next = new Set(prev);
      if (next.has(modulo)) {
        next.delete(modulo);
      } else {
        next.add(modulo);
      }
      return next;
    });
  };

  // Group permissions by module
  const permissoesPorModulo = permissoesUsuario.reduce((acc, perm) => {
    if (!acc[perm.modulo]) {
      acc[perm.modulo] = [];
    }
    acc[perm.modulo].push(perm);
    return acc;
  }, {} as Record<string, PermissaoEfetiva[]>);

  const modulos = Object.keys(permissoesPorModulo).sort();

  return (
    <div className="grid grid-cols-[25%_75%] gap-4">
      {/* Left Column - Users List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Usuários ({usuariosFiltrados.length}{searchTerm ? ` de ${usuarios.length}` : ''})
          </h3>
          {/* Search field */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
                title="Limpar busca"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
          {loadingUsuario && usuarios.length === 0 ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Carregando usuários...</p>
            </div>
          ) : usuariosFiltrados.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {searchTerm ? 'Nenhum usuário encontrado para a busca' : 'Nenhum usuário encontrado'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Limpar busca
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {usuariosFiltrados.map(user => (
                <button
                  key={user.id}
                  onClick={() => onSelectUsuario(user)}
                  className={`w-full px-4 py-3 text-left transition-colors ${
                    selectedUsuario?.id === user.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      user.ativo ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {user.username}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                      <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                        {user.role}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Selected user permissions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {!selectedUsuario ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Selecione um usuário</p>
            <p className="text-sm">Escolha um usuário da lista ao lado para visualizar e editar suas permissões</p>
          </div>
        ) : (
          <>
            {/* Header with user info */}
            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {selectedUsuario.username}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {selectedUsuario.email} • <span className="font-semibold text-blue-600 dark:text-blue-400">{selectedUsuario.role}</span>
                  </p>
                </div>
                <div className="text-right text-xs text-gray-600 dark:text-gray-400">
                  <p>{permissoesUsuario.length} telas</p>
                  <p>{permissoesUsuario.filter(p => p.origem === 'usuario').length} customizadas</p>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-gray-600 dark:text-gray-400">Do perfil</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-orange-500" />
                  <span className="text-gray-600 dark:text-gray-400">Customizada</span>
                </div>
                <div className="ml-auto flex gap-2">
                  {tiposPermissao.map(tipo => (
                    <span key={tipo.key} className="text-gray-600 dark:text-gray-400" title={tipo.title}>
                      {tipo.label} = {tipo.title}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Permissions grouped by module with accordion */}
            <div className="overflow-y-auto max-h-[calc(100vh-400px)]">
              {loadingUsuario ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Carregando permissões...</p>
                </div>
              ) : permissoesUsuario.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma permissão encontrada</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {modulos.map(modulo => (
                    <div key={modulo}>
                      {/* Module header - accordion */}
                      <button
                        onClick={() => toggleModule(modulo)}
                        className="w-full bg-gray-100 dark:bg-gray-700 px-4 py-2 flex items-center justify-between hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {modulo} ({permissoesPorModulo[modulo].length})
                        </h4>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                            expandedModulos.has(modulo) ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {/* Module screens */}
                      {expandedModulos.has(modulo) && (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                          {permissoesPorModulo[modulo].map(perm => (
                            <div key={perm.tela_id} className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <div className="flex items-center gap-3">
                                {/* Origin indicator */}
                                <div
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    perm.origem === 'usuario' ? 'bg-orange-500' : 'bg-blue-500'
                                  }`}
                                  title={perm.origem === 'usuario' ? 'Permissão customizada' : 'Permissão do perfil'}
                                />

                                {/* Screen name */}
                                <span className="text-sm text-gray-900 dark:text-white flex-1 min-w-0 truncate">
                                  {perm.tela_nome}
                                </span>

                                {/* Permission buttons */}
                                <div className="flex gap-1 flex-shrink-0">
                                  {tiposPermissao.map(tipo => {
                                    const hasPermissao = perm[tipo.key];
                                    return (
                                      <button
                                        key={tipo.key}
                                        onClick={() => onPermissaoChange(perm.tela_id, tipo.key, !hasPermissao)}
                                        className={`w-6 h-6 rounded text-xs font-bold transition-all ${
                                          hasPermissao
                                            ? `bg-${tipo.color}-600 text-white shadow-sm`
                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                                        } hover:scale-110`}
                                        title={`${tipo.title}: ${hasPermissao ? 'Remover' : 'Conceder'}`}
                                      >
                                        {tipo.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
